<template>
  <div
    class="add-qualification bg-background"
    :class="{ invisible: isCameraActive }"
  >
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="$router.back()" />
      </template>
      証明書 - 資格証明書
    </Header>
    <div class="pt-2 pb-4 mx-3">
      <StepBar :maxValue="4" :currentValue="2" class="mb-4" />
      <CustomValidationObserver v-slot="{ handleSubmit }">
        <AddQualification
          v-model="inputValues"
          :qualificationOptions="filteredQualificationOptions"
          v-model:isCameraActive="isCameraActive"
        />
        <BottomFormActionButtons
          submitButtonText="次へ"
          @submit="handleSubmit(onClickSubmit)"
          :disabled="isSubmitting"
          hideSkipButton
        />
      </CustomValidationObserver>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-facing-decorator";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import Header from "@/components/organisms/common/Header.vue";
import { registerStore } from "@/store/register";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { accountService } from "@/app/services/AccountService";
import { UserQualification } from "@/types/qualification/UserQualification";
import StepBar from "@/components/molecules/other/StepBar.vue";
import BottomFormActionButtons from "@/components/organisms/register/BottomFormActionButtons.vue";
import { UserQualificationInputValues } from "@/types/qualification/EditableUserQualificationItems";
import AddQualification from "@/components/organisms/register/AddQualification.vue";
import { Qualification } from "@/types/Master";
import { authStore } from "@/store/auth";

@Component({
  components: {
    FeatherIcon,
    Header,
    StepBar,
    BottomFormActionButtons,
    AddQualification
  }
})
export default class RegisterAddQualification extends Vue {
  inputValues: UserQualificationInputValues = {
    image: null,
    qualification: null
  };
  registeredQualifications: UserQualification[] = [];
  isCameraActive = false;
  isSubmitting = false;

  get selectedQualifications() {
    return registerStore.qualifications;
  }

  get filteredQualificationOptions(): Qualification[] {
    // 既に登録済みの資格は省く
    const filteredQualificationOptions = authStore.product.qualifications.filter(
      qualification => {
        const masterQualificationId = qualification.id;

        // すでに入力されていないか
        const isAlreadyInputted = this.selectedQualifications.some(
          selected => selected.qualification === masterQualificationId
        );

        // すでに登録されていないか
        const isAlreadyRegistered = this.registeredQualifications.some(
          registered => registered.qualification === masterQualificationId
        );

        return !isAlreadyInputted && !isAlreadyRegistered;
      }
    );

    return filteredQualificationOptions;
  }

  async created() {
    this.$loading.show();
    try {
      await this.fetchRegisteredQualification();
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async fetchRegisteredQualification() {
    const { results } = await accountService.fetchQualifications();
    this.registeredQualifications = results;
  }

  async onClickSubmit() {
    if (this.isSubmitting) return;

    if (!this.inputValues.image) {
      await this.$bvModal.msgBoxOk(
        "資格証を撮影、またはファイル選択してください。",
        {
          centered: true
        }
      );
      return;
    }
    this.isSubmitting = true;

    const body = {
      image: this.inputValues.image,
      qualification: this.inputValues.qualification!
    };

    const isOk = await registerStore.addQualification(body);

    // 追加に失敗した場合
    if (!isOk) {
      await this.$bvModal.msgBoxOk("この資格は既に追加されています。", {
        centered: true
      });
    }

    this.isSubmitting = false;
    this.$router.back();
  }
}
</script>

<style lang="scss" scoped>
.add-qualification {
  min-height: inherit;
}
</style>
